import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { routes } from '#apps/client-web/app/routes';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type AuditId } from '#packages/data-collection/models/audit';
import { type AuditWaterFixtureForm } from '#packages/data-collection/models/audit-water-fixture-form';

export type AuditFormWaterFixturePageViewModel = ReturnType<
  typeof useAuditFormWaterFixturePageViewModel
>;

type AuditFormWaterFixturePageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormWaterFixturePageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormWaterFixturePageViewModelProps) => {
  const { auditId } = useParams<{ auditId: AuditId }>();
  const navigate = useNavigate();

  const auditFormQuery = useLoadAuditFormByType<AuditWaterFixtureForm>({
    auditId: auditId!,
    queryClient,
    formType: 'water-fixture',
    auditFormService,
  });

  const thumbnailFileIds = useMemo(
    () =>
      (auditFormQuery.data?.entries ?? []).map(
        (entry) => entry.attachments?.[0]?.name ?? null,
      ),
    [auditFormQuery.data?.entries],
  );

  const uniqueThumbnailFileIds = useMemo(
    () =>
      Array.from(
        new Set(thumbnailFileIds.filter((id): id is string => id !== null)),
      ),
    [thumbnailFileIds],
  );

  const thumbnailQueries = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds: uniqueThumbnailFileIds,
  });

  const thumbnailMap = useMemo<Record<string, FileContent | null>>(() => {
    const map: Record<string, FileContent | null> = {};
    uniqueThumbnailFileIds.forEach((fileId, i) => {
      map[fileId] = thumbnailQueries[i]?.data ?? null;
    });
    return map;
  }, [uniqueThumbnailFileIds, thumbnailQueries]);

  const onCreate = useCallback(() => {
    logger?.info('Navigating to create audit water fixture form entry page');
    navigate(
      routes.audit.getEnergyAuditWaterFixtureFormEntryPath(auditId!, -1),
    );
  }, [navigate, auditId]);

  return {
    auditId,
    auditFormQuery,
    thumbnailFileIds,
    thumbnailMap,
    onCreate,
  };
};
